import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { Card } from "@/components/ui/atoms/Card";
import { Typography } from "@/components/ui/atoms/Typography";
import { BotAvatar } from "./BotAvatar";
import { TypingIndicator } from "./TypingIndicator";
import { formatTime } from "@/utils/date";

const MARKDOWN_COMPONENTS = {
  p: ({ children }: { children?: ReactNode }) => (
    <p className="leading-relaxed">{children}</p>
  ),
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="ml-5 list-disc leading-relaxed marker:text-purple">{children}</ul>
  ),
  ol: ({ children }: { children?: ReactNode }) => (
    <ol className="ml-5 list-decimal leading-relaxed marker:font-semibold marker:text-purple">{children}</ol>
  ),
  li: ({ children }: { children?: ReactNode }) => (
    <li className="pl-1 leading-relaxed">{children}</li>
  ),
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  a: ({ children, href }: { children?: ReactNode; href?: string }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
      {children}
    </a>
  ),
  code: ({ children }: { children?: ReactNode }) => (
    <code className="rounded bg-dark-invirtus-soft px-1 py-0.5 font-mono text-[0.85em]">
      {children}
    </code>
  ),
};

interface ChatMessage {
  id: string;
  role: "user" | "bot";
  content: string;
  isError: boolean;
  timestamp: Date | string;
  /** Optional extra Tailwind classes for the bubble (e.g. min-width overrides for welcome messages). */
  bubbleClassName?: string;
}

interface MessageGroupProps {
  messages: ChatMessage[];
  role: "user" | "bot";
  showAvatar: boolean;
  timestamp: Date | string;
  showTypingIndicator?: boolean;
  /** When false, the "16:08"-style timestamp under the group is hidden. Defaults to true. */
  showTimestamp?: boolean;
}

export function MessageGroup({
  messages,
  role,
  showAvatar,
  timestamp,
  showTypingIndicator = false,
  showTimestamp = true,
}: MessageGroupProps) {
  const isBot = role === "bot";

  return (
    <div className={`flex gap-2 ${isBot ? "justify-start" : "justify-end"}`}>
      {isBot && showAvatar ? (
        <BotAvatar size="sm" />
      ) : isBot ? (
        <span className="w-8 shrink-0" />
      ) : null}

      <div className={`flex w-full flex-col gap-1 ${isBot ? "items-start" : "items-end"}`}>
        {!showTypingIndicator
          ? messages.map((message) => {
              const bubble =
                message.role === "user"
                  ? "!m-0 ml-auto max-w-[80%] border-0 bg-purple shadow-none"
                  : message.isError
                    ? "!m-0 max-w-[80%] border border-red bg-red-soft shadow-none"
                    : "!m-0 max-w-[80%] border-0 bg-background-gray shadow-none";

              return (
                <Card
                  key={message.id}
                  variant="custom"
                  padding="none"
                  rounded="lg"
                  className={`${bubble} px-3.5 py-2.5 ${message.bubbleClassName ?? ""}`}
                >
                  {message.role === "bot" && !message.isError ? (
                    <div className="text-sm font-medium leading-relaxed text-primary [&>*]:my-0 [&>*+*]:mt-2 [&_li+li]:mt-1">
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <Typography variant="body-sm" color="primary" weight="medium" as="p" className="whitespace-pre-line leading-5">
                      {message.content}
                    </Typography>
                  )}
                </Card>
              );
            })
          : <TypingIndicator />}

        {!showTypingIndicator && showTimestamp ? (
          <Typography variant="body-xs" color="muted" as="span" weight="semibold" className="px-0.5">
            {formatTime(timestamp)}
          </Typography>
        ) : null}
      </div>
    </div>
  );
}
